import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ExternalLink,
    Calculator,
    Globe,
    Loader2,
    Palette,
    Phone,
    RefreshCw,
    Save,
    Settings2,
    Sparkles,
    ToggleLeft,
    ToggleRight,
} from "lucide-react";
import { toast } from "sonner";
import { akilSiteWidgetGet, akilSiteWidgetSave } from "../../lib/akilApi";
import AkilWidgetModulMerkezi from "./AkilWidgetModulMerkezi";

const TEMA_KEYS = [
    { key: "gold", label: "Altın vurgu" },
    { key: "navy", label: "Lacivert" },
    { key: "burgundy", label: "Bordo" },
    { key: "paper", label: "Arka plan" },
    { key: "radius", label: "Köşe yuvarlaklığı" },
];

const KANAL_KEYS = [
    { key: "telefon", label: "Telefon" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "telegram", label: "Telegram" },
    { key: "basvuru", label: "Başvuru formu" },
];

function Field({ label, children, hint }) {
    return (
        <label className="block space-y-1.5">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
            {children}
            {hint ? <span className="text-[11px] text-slate-600">{hint}</span> : null}
        </label>
    );
}

function inputCls(extra = "") {
    return `w-full rounded-lg border border-white/10 bg-[#0B1220] px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[#D4AF37]/50 focus:outline-none ${extra}`;
}

export default function AkilSiteWidgetPanel() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [ayarlar, setAyarlar] = useState(null);
    const [varsayilan, setVarsayilan] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [onizlemeUrl, setOnizlemeUrl] = useState("https://mbshukuk.com/");
    const [katalog, setKatalog] = useState([]);
    const [seciliModul, setSeciliModul] = useState("dosya_sorgu_otp");
    const [panelTab, setPanelTab] = useState("moduller");

    const yukle = useCallback(async () => {
        setLoading(true);
        try {
            const d = await akilSiteWidgetGet(7);
            const merged = {
                ...(d.ayarlar || {}),
                deger_kaybi: {
                    ...(d.varsayilan?.deger_kaybi || {}),
                    ...(d.ayarlar?.deger_kaybi || {}),
                },
                moduller: d.moduller || d.ayarlar?.moduller || {},
            };
            setAyarlar(merged);
            setKatalog(d.modul_katalog || []);
            setVarsayilan(d.varsayilan || {});
            setAnalytics(d.analytics || null);
            setOnizlemeUrl(d.onizleme_url || "https://mbshukuk.com/");
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Site widget ayarları yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        yukle();
    }, [yukle]);

    const patch = (path, value) => {
        setAyarlar((prev) => {
            const next = structuredClone(prev || {});
            const parts = path.split(".");
            let cur = next;
            for (let i = 0; i < parts.length - 1; i += 1) {
                if (!cur[parts[i]] || typeof cur[parts[i]] !== "object") cur[parts[i]] = {};
                cur = cur[parts[i]];
            }
            cur[parts[parts.length - 1]] = value;
            return next;
        });
    };

    const kaydet = async () => {
        setSaving(true);
        try {
            const payload = structuredClone(ayarlar);
            const dkAktif = payload.deger_kaybi?.rehber_aktif !== false;
            if (!payload.moduller) payload.moduller = {};
            if (!payload.moduller.deger_kaybi_rehber) payload.moduller.deger_kaybi_rehber = {};
            payload.moduller.deger_kaybi_rehber.aktif = dkAktif;
            const d = await akilSiteWidgetSave(payload);
            setAyarlar(d.ayarlar || ayarlar);
            toast.success("Site widget ayarları kaydedildi — mbshukuk.com birkaç saniye içinde güncellenir.");
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    const sifirla = () => {
        if (varsayilan) {
            setAyarlar(structuredClone(varsayilan));
            toast.message("Varsayılan ayarlar yüklendi — kaydetmeyi unutmayın.");
        }
    };

    const previewStyle = useMemo(() => {
        const t = ayarlar?.tema || {};
        return {
            "--akil-gold": t.gold || "#c9a227",
            "--akil-navy": t.navy || "#0e1a2b",
            "--akil-burgundy": t.burgundy || "#8b1f2f",
            "--akil-paper": t.paper || "#f7f5f2",
            "--akil-radius": t.radius || "1.35rem",
        };
    }, [ayarlar]);

    if (loading || !ayarlar) {
        return (
            <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" /> Site widget yükleniyor…
            </div>
        );
    }

    const tel = ayarlar.iletisim?.telefon || "0505 115 34 79";

    return (
        <div className="space-y-6">
            <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#121A2F] to-[#0B1220] p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[#D4AF37]">
                            <Globe className="h-5 w-5" />
                            <h2 className="font-display text-xl text-white">Site Widget Kontrol Merkezi</h2>
                        </div>
                        <p className="mt-2 text-sm text-slate-400 max-w-2xl">
                            mbshukuk.com sağ alt köşedeki Akıl widget — renk, başlık, iletişim kanalları ve
                            hata mesajı dahil tam kontrol. Panel içi widget değil; canlı site widget&apos;ıdır.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={yukle} className="px-3 py-2 rounded-lg border border-white/10 text-sm hover:border-white/20">
                            <RefreshCw className="h-4 w-4 inline mr-1" /> Yenile
                        </button>
                        <a href={onizlemeUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-lg border border-[#D4AF37]/30 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10">
                            <ExternalLink className="h-4 w-4 inline mr-1" /> Canlı site
                        </a>
                        <button type="button" onClick={sifirla} className="px-3 py-2 rounded-lg border border-white/10 text-sm hover:border-white/20">
                            Varsayılan
                        </button>
                        <button type="button" onClick={kaydet} disabled={saving} className="px-4 py-2 rounded-lg bg-[#D4AF37] text-[#0B1220] text-sm font-semibold hover:bg-[#e5c04a] disabled:opacity-60">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : <Save className="h-4 w-4 inline mr-1" />}
                            Kaydet
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                {[
                    { id: "moduller", label: "Modül Merkezi" },
                    { id: "tasarim", label: "Tasarım & Metin" },
                    { id: "deger_kaybi", label: "Değer Kaybı" },
                ].map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setPanelTab(t.id)}
                        className={`px-4 py-2 rounded-full text-sm border ${
                            panelTab === t.id
                                ? "bg-[#D4AF37]/20 border-[#D4AF37]/50 text-[#D4AF37]"
                                : "border-white/10 text-slate-400"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {panelTab === "moduller" && (
                <AkilWidgetModulMerkezi
                    katalog={katalog}
                    moduller={ayarlar.moduller || {}}
                    patch={patch}
                    seciliModul={seciliModul}
                    onSelectModul={setSeciliModul}
                />
            )}

            {panelTab !== "moduller" && (
            <div className="grid xl:grid-cols-[1.2fr_0.8fr] gap-6 mt-6">
                <div className="space-y-5">
                    {panelTab === "tasarim" && (
                    <>
                    <section className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-4 space-y-4">
                        <div className="flex items-center gap-2 text-white">
                            <Settings2 className="h-4 w-4 text-[#D4AF37]" />
                            <h3 className="font-medium">Genel & Davranış</h3>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2">
                            <span className="text-sm">Widget aktif (mbshukuk.com)</span>
                            <button type="button" onClick={() => patch("aktif", !ayarlar.aktif)} className="text-[#D4AF37]">
                                {ayarlar.aktif ? <ToggleRight className="h-7 w-7" /> : <ToggleLeft className="h-7 w-7 text-slate-500" />}
                            </button>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2">
                            <div>
                                <span className="text-sm block">Async sohbet (Celery kuyruk)</span>
                                <span className="text-[11px] text-slate-500">Kapalıyken anında yanıt — bağlantı hatası riski düşük</span>
                            </div>
                            <button type="button" onClick={() => patch("async_chat", !ayarlar.async_chat)} className="text-[#D4AF37]">
                                {ayarlar.async_chat ? <ToggleRight className="h-7 w-7" /> : <ToggleLeft className="h-7 w-7 text-slate-500" />}
                            </button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Marka / AI adı">
                                <input className={inputCls()} value={ayarlar.marka || ""} onChange={(e) => patch("marka", e.target.value)} />
                            </Field>
                            <Field label="Tagline (alt satır)">
                                <input className={inputCls()} value={ayarlar.tagline || ""} onChange={(e) => patch("tagline", e.target.value)} />
                            </Field>
                            <Field label="Hero kicker">
                                <input className={inputCls()} value={ayarlar.hero_kicker || ""} onChange={(e) => patch("hero_kicker", e.target.value)} />
                            </Field>
                            <Field label="Launcher başlık">
                                <input className={inputCls()} value={ayarlar.launcher_baslik || ""} onChange={(e) => patch("launcher_baslik", e.target.value)} />
                            </Field>
                            <Field label="Launcher alt metin" hint="Sağ alttaki küçük buton">
                                <input className={inputCls()} value={ayarlar.launcher_alt || ""} onChange={(e) => patch("launcher_alt", e.target.value)} />
                            </Field>
                            <Field label="Yetenek etiketleri" hint="Virgülle ayırın">
                                <input
                                    className={inputCls()}
                                    value={(ayarlar.yetenekler || []).join(", ")}
                                    onChange={(e) => patch("yetenekler", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                                />
                            </Field>
                        </div>
                        <Field label="Davet / invite metni">
                            <textarea className={inputCls("min-h-[72px]")} value={ayarlar.invite_metin || ""} onChange={(e) => patch("invite_metin", e.target.value)} />
                        </Field>
                    </section>

                    <section className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-4 space-y-4">
                        <div className="flex items-center gap-2 text-white">
                            <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                            <h3 className="font-medium">Promo Popup</h3>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Promo başlık">
                                <input className={inputCls()} value={ayarlar.promo_baslik || ""} onChange={(e) => patch("promo_baslik", e.target.value)} />
                            </Field>
                            <Field label="Promo alt başlık">
                                <input className={inputCls()} value={ayarlar.promo_alt_baslik || ""} onChange={(e) => patch("promo_alt_baslik", e.target.value)} />
                            </Field>
                            <Field label="Promo metin">
                                <textarea className={inputCls("min-h-[64px]")} value={ayarlar.promo_metin || ""} onChange={(e) => patch("promo_metin", e.target.value)} />
                            </Field>
                            <div className="space-y-3">
                                <Field label="CTA — Bilgi merkezi">
                                    <input className={inputCls()} value={ayarlar.promo_cta || ""} onChange={(e) => patch("promo_cta", e.target.value)} />
                                </Field>
                                <Field label="CTA — Sohbet">
                                    <input className={inputCls()} value={ayarlar.promo_cta_sohbet || ""} onChange={(e) => patch("promo_cta_sohbet", e.target.value)} />
                                </Field>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-4 space-y-4">
                        <div className="flex items-center gap-2 text-white">
                            <Phone className="h-4 w-4 text-[#D4AF37]" />
                            <h3 className="font-medium">İletişim & Kanallar</h3>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Telefon (görünen)">
                                <input className={inputCls()} value={ayarlar.iletisim?.telefon || ""} onChange={(e) => patch("iletisim.telefon", e.target.value)} />
                            </Field>
                            <Field label="Telefon E.164">
                                <input className={inputCls()} value={ayarlar.iletisim?.telefon_e164 || ""} onChange={(e) => patch("iletisim.telefon_e164", e.target.value)} />
                            </Field>
                            <Field label="WhatsApp E.164">
                                <input className={inputCls()} value={ayarlar.iletisim?.whatsapp_e164 || ""} onChange={(e) => patch("iletisim.whatsapp_e164", e.target.value)} />
                            </Field>
                            <Field label="E-posta">
                                <input className={inputCls()} value={ayarlar.iletisim?.email || ""} onChange={(e) => patch("iletisim.email", e.target.value)} />
                            </Field>
                            <Field label="Telegram URL">
                                <input className={inputCls()} value={ayarlar.iletisim?.telegram_url || ""} onChange={(e) => patch("iletisim.telegram_url", e.target.value)} />
                            </Field>
                            <Field label="Başvuru URL">
                                <input className={inputCls()} value={ayarlar.iletisim?.basvuru_url || ""} onChange={(e) => patch("iletisim.basvuru_url", e.target.value)} />
                            </Field>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {KANAL_KEYS.map(({ key, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => patch(`kanallar.${key}`, !ayarlar.kanallar?.[key])}
                                    className={`px-3 py-1.5 rounded-full text-xs border ${ayarlar.kanallar?.[key] ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-white/10 text-slate-500"}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <Field label="Composer alt ipucu">
                            <input className={inputCls()} value={ayarlar.composer_hint || ""} onChange={(e) => patch("composer_hint", e.target.value)} />
                        </Field>
                        <Field label="Bağlantı hatası mesajı" hint="{telefon} yer tutucusu kullanın">
                            <textarea className={inputCls("min-h-[64px]")} value={ayarlar.hata_mesaji || ""} onChange={(e) => patch("hata_mesaji", e.target.value)} />
                        </Field>
                    </section>

                    <section className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-4 space-y-4">
                        <div className="flex items-center gap-2 text-white">
                            <Palette className="h-4 w-4 text-[#D4AF37]" />
                            <h3 className="font-medium">Tema Renkleri</h3>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {TEMA_KEYS.map(({ key, label }) => (
                                <Field key={key} label={label}>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            className="h-10 w-12 rounded border border-white/10 bg-transparent cursor-pointer"
                                            value={(ayarlar.tema?.[key] || "#c9a227").startsWith("rgba") ? "#c9a227" : (ayarlar.tema?.[key] || "#c9a227")}
                                            onChange={(e) => patch(`tema.${key}`, e.target.value)}
                                        />
                                        <input className={inputCls()} value={ayarlar.tema?.[key] || ""} onChange={(e) => patch(`tema.${key}`, e.target.value)} />
                                    </div>
                                </Field>
                            ))}
                        </div>
                    </section>
                    </>
                    )}

                    {panelTab === "deger_kaybi" && (
                    <section className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-4">
                        <div className="flex items-center gap-2 text-white">
                            <Calculator className="h-4 w-4 text-emerald-400" />
                            <h3 className="font-medium">Değer Kaybı Rehberi (Akıl Sihirbazı)</h3>
                        </div>
                        <p className="text-xs text-slate-400">
                            Kullanıcı &quot;değer kaybı hesapla&quot; dediğinde Akıl adım adım soru sorar ve MBS hibrit motoru ile hesaplar.
                            Düşük sonuçları önlemek için fatura tabanlı hasar katsayısı ve minimum taban oranı buradan ayarlanır.
                        </p>
                        <div className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2">
                            <span className="text-sm">Rehberli hesaplama aktif</span>
                            <button
                                type="button"
                                onClick={() => patch("deger_kaybi.rehber_aktif", !ayarlar.deger_kaybi?.rehber_aktif)}
                                className="text-emerald-400"
                            >
                                {ayarlar.deger_kaybi?.rehber_aktif !== false ? (
                                    <ToggleRight className="h-7 w-7" />
                                ) : (
                                    <ToggleLeft className="h-7 w-7 text-slate-500" />
                                )}
                            </button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Hesap yöntemi">
                                <select
                                    className={inputCls()}
                                    value={ayarlar.deger_kaybi?.varsayilan_yontem || "karsilastirma"}
                                    onChange={(e) => patch("deger_kaybi.varsayilan_yontem", e.target.value)}
                                >
                                    <option value="karsilastirma">Karşılaştırma (3 yöntem)</option>
                                    <option value="resmi">Resmi tablo (sigorta)</option>
                                    <option value="mbs_hibrit">MBS Hibrit</option>
                                    <option value="serbest_piyasa">Serbest piyasa (Yargıtay)</option>
                                    <option value="hibrit">Hibrit (eski alias)</option>
                                </select>
                            </Field>
                            <Field label="Varsayılan hasar şiddeti">
                                <select
                                    className={inputCls()}
                                    value={ayarlar.deger_kaybi?.varsayilan_hasar_durumu || "orta"}
                                    onChange={(e) => patch("deger_kaybi.varsayilan_hasar_durumu", e.target.value)}
                                >
                                    <option value="hafif">Hafif</option>
                                    <option value="orta">Orta</option>
                                    <option value="agir">Ağır</option>
                                    <option value="cok_agir">Çok ağır</option>
                                </select>
                            </Field>
                            <Field label="Fatura üst sınır oranı" hint="0.92 = hasar faturasının %92'si">
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.5"
                                    max="1"
                                    className={inputCls()}
                                    value={ayarlar.deger_kaybi?.fatura_cap_orani ?? 0.92}
                                    onChange={(e) => patch("deger_kaybi.fatura_cap_orani", parseFloat(e.target.value) || 0.92)}
                                />
                            </Field>
                            <Field label="Min. hasar katsayısı (faturadan)" hint="Eksik parça bilgisinde taban">
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.1"
                                    max="0.8"
                                    className={inputCls()}
                                    value={ayarlar.deger_kaybi?.min_hasar_k_fatura ?? 0.24}
                                    onChange={(e) => patch("deger_kaybi.min_hasar_k_fatura", parseFloat(e.target.value) || 0.24)}
                                />
                            </Field>
                            <Field label="Min. sonuç / baz oranı" hint="Çok düşük sonuç tabanı">
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="0.5"
                                    className={inputCls()}
                                    value={ayarlar.deger_kaybi?.min_gercek_oran_baz ?? 0.18}
                                    onChange={(e) => patch("deger_kaybi.min_gercek_oran_baz", parseFloat(e.target.value) || 0.18)}
                                />
                            </Field>
                        </div>
                        <Field label="Başlangıç mesajı (sihirbaz)">
                            <textarea
                                className={inputCls("min-h-[88px]")}
                                value={ayarlar.deger_kaybi?.baslangic_mesaji || ""}
                                onChange={(e) => patch("deger_kaybi.baslangic_mesaji", e.target.value)}
                            />
                        </Field>
                        <Field label="Örnek tam mesaj">
                            <textarea
                                className={inputCls("min-h-[64px] font-mono text-xs")}
                                value={ayarlar.deger_kaybi?.ornek_tam_mesaj || ""}
                                onChange={(e) => patch("deger_kaybi.ornek_tam_mesaj", e.target.value)}
                            />
                        </Field>
                        <Field label="Hızlı tetik cümleleri" hint="Virgülle ayırın — widget önerilerine eklenir">
                            <input
                                className={inputCls()}
                                value={(ayarlar.deger_kaybi?.hizli_tetikler || []).join(", ")}
                                onChange={(e) =>
                                    patch(
                                        "deger_kaybi.hizli_tetikler",
                                        e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
                                    )
                                }
                            />
                        </Field>
                    </section>
                    )}
                </div>

                <div className="space-y-5">
                    <section className="rounded-xl border border-white/10 overflow-hidden" style={previewStyle}>
                        <div className="px-4 py-3 border-b border-white/10 bg-[#0B1220] text-sm text-slate-400">Canlı önizleme (yaklaşık)</div>
                        <div className="p-4 bg-[var(--akil-paper)] min-h-[420px] relative">
                            <div
                                className="ml-auto w-full max-w-[320px] rounded-[var(--akil-radius)] overflow-hidden shadow-2xl border border-black/10"
                                style={{ background: "var(--akil-navy)", color: "#fff" }}
                            >
                                <div className="px-4 py-3 border-b border-white/10" style={{ background: "linear-gradient(135deg, var(--akil-navy), var(--akil-burgundy))" }}>
                                    <div className="text-[10px] uppercase tracking-wider opacity-70">{ayarlar.hero_kicker}</div>
                                    <div className="font-semibold text-lg">{ayarlar.marka}</div>
                                    <div className="text-xs opacity-80 mt-1">{ayarlar.tagline}</div>
                                </div>
                                <div className="p-3 space-y-2">
                                    <div className="flex flex-wrap gap-1">
                                        {(ayarlar.yetenekler || []).slice(0, 4).map((y) => (
                                            <span key={y} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--akil-gold)", color: "var(--akil-navy)" }}>{y}</span>
                                        ))}
                                    </div>
                                    <div className="rounded-lg bg-white/5 p-2 text-xs leading-relaxed">{ayarlar.invite_metin || "Davet metni…"}</div>
                                </div>
                                <div className="grid grid-cols-2 gap-1 p-2 border-t border-white/10 text-[11px]">
                                    {ayarlar.kanallar?.telefon !== false && <span className="text-center py-1 rounded bg-white/5">{tel}</span>}
                                    {ayarlar.kanallar?.whatsapp !== false && <span className="text-center py-1 rounded bg-emerald-600/30">WhatsApp</span>}
                                </div>
                            </div>
                            <div
                                className="absolute right-4 bottom-4 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg text-sm"
                                style={{ background: "var(--akil-navy)", color: "#fff", border: "1px solid var(--akil-gold)" }}
                            >
                                <span className="font-semibold">{ayarlar.launcher_baslik || ayarlar.marka}</span>
                            </div>
                        </div>
                    </section>

                    {analytics?.olaylar?.length ? (
                        <section className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-4">
                            <h3 className="text-sm font-medium text-white mb-3">Son 7 gün widget analytics</h3>
                            <ul className="space-y-1.5 text-sm">
                                {analytics.olaylar.slice(0, 8).map((o) => (
                                    <li key={o.olay} className="flex justify-between text-slate-400">
                                        <span>{o.olay}</span>
                                        <span className="text-[#D4AF37]">{o.adet}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null}
                </div>
            </div>
            )}
        </div>
    );
}
